/* ============================================================
   custom-promo-tabs.js
   Custom Promo Tab System
   ============================================================ */

(function () {
  'use strict';

  /* ----------------------------------------------------------
     Helpers
     ---------------------------------------------------------- */
  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.cptMoneyFormat || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function t(key, replacements) {
    const translations = window.cptTranslations || {};
    let str = translations[key] || key;
    if (replacements) {
      Object.keys(replacements).forEach(function (k) {
        var val = replacements[k];
        str = str.replace('%' + k + '%', val);
        str = str.replace('[' + k + ']', val);
        str = str.replace('{{ ' + k + ' }}', val);
      });
    }
    return str;
  }

  function normalizeDiscountType(discountType) {
    if (discountType === 'bundle_price') return 'fixed_price';
    if (discountType === 'fixed') return 'fixed_amount';
    if (discountType === 'amount_off') return 'fixed_amount';
    if (discountType === 'dollar_off') return 'fixed_amount';
    if (discountType === 'price_off') return 'fixed_amount';
    return discountType || '';
  }

  function isUnavailableChoice(el) {
    return !el || el.classList.contains('is-unavailable') || el.dataset.available === 'false';
  }

  function markSelectedChip(chips, selectedChip) {
    chips.forEach(function (chip) {
      chip.classList.toggle('is-selected', chip === selectedChip);
    });
  }

  function stabilizeInitialScroll() {
    if (window.location.hash) return;
    if (window.Shopify && window.Shopify.designMode) return;

    var startingY = window.scrollY || window.pageYOffset || 0;
    var userInteracted = false;
    var settled = false;
    var nativeScrollTo = window.scrollTo ? window.scrollTo.bind(window) : null;
    var nativeScrollBy = window.scrollBy ? window.scrollBy.bind(window) : null;
    var nativeScrollIntoView = window.HTMLElement && window.HTMLElement.prototype
      ? window.HTMLElement.prototype.scrollIntoView
      : null;
    var section = document.querySelector('.cpt-section');
    var sectionTop = section ? (section.getBoundingClientRect().top + window.scrollY) : 0;
    var maxAllowedY = Math.max(startingY, sectionTop - 80);

    function markInteracted() {
      userInteracted = true;
      cleanup();
    }

    function cleanup() {
      if (settled) return;
      settled = true;
      window.removeEventListener('wheel', markInteracted, true);
      window.removeEventListener('touchstart', markInteracted, true);
      window.removeEventListener('mousedown', markInteracted, true);
      window.removeEventListener('keydown', markInteracted, true);
      if (nativeScrollTo) {
        window.scrollTo = nativeScrollTo;
      }
      if (nativeScrollBy) {
        window.scrollBy = nativeScrollBy;
      }
      if (nativeScrollIntoView) {
        window.HTMLElement.prototype.scrollIntoView = nativeScrollIntoView;
      }
    }

    function enforceScroll() {
      if (settled || userInteracted) return;
      var currentY = window.scrollY || window.pageYOffset || 0;
      if (currentY > maxAllowedY + 120) {
        window.scrollTo(0, startingY);
      }
    }

    if (nativeScrollTo) {
      window.scrollTo = function scrollToGuard() {
        if (settled || userInteracted) {
          return nativeScrollTo.apply(window, arguments);
        }

        var targetY = startingY;
        if (arguments.length === 1 && typeof arguments[0] === 'object' && arguments[0] !== null) {
          if (typeof arguments[0].top === 'number') {
            targetY = arguments[0].top;
          }
        } else if (arguments.length >= 2 && typeof arguments[1] === 'number') {
          targetY = arguments[1];
        }

        if (targetY > maxAllowedY + 120) {
          return nativeScrollTo(0, startingY);
        }

        return nativeScrollTo.apply(window, arguments);
      };
    }

    if (nativeScrollBy) {
      window.scrollBy = function scrollByGuard() {
        if (settled || userInteracted) {
          return nativeScrollBy.apply(window, arguments);
        }

        var deltaY = 0;
        if (arguments.length === 1 && typeof arguments[0] === 'object' && arguments[0] !== null) {
          deltaY = Number(arguments[0].top || 0);
        } else if (arguments.length >= 2) {
          deltaY = Number(arguments[1] || 0);
        }

        if (deltaY > 120) {
          return nativeScrollTo(0, startingY);
        }

        return nativeScrollBy.apply(window, arguments);
      };
    }

    if (nativeScrollIntoView) {
      window.HTMLElement.prototype.scrollIntoView = function scrollIntoViewGuard() {
        if (settled || userInteracted) {
          return nativeScrollIntoView.apply(this, arguments);
        }

        var rect = this && this.getBoundingClientRect ? this.getBoundingClientRect() : null;
        var absoluteTop = rect ? rect.top + (window.scrollY || window.pageYOffset || 0) : startingY;
        if (absoluteTop > maxAllowedY + 120) {
          return nativeScrollTo ? nativeScrollTo(0, startingY) : undefined;
        }

        return nativeScrollIntoView.apply(this, arguments);
      };
    }

    window.addEventListener('wheel', markInteracted, true);
    window.addEventListener('touchstart', markInteracted, true);
    window.addEventListener('mousedown', markInteracted, true);
    window.addEventListener('keydown', markInteracted, true);

    [80, 220, 450, 900, 1500].forEach(function (delay) {
      window.setTimeout(enforceScroll, delay);
    });

    window.setTimeout(cleanup, 1800);
  }

  /* ----------------------------------------------------------
     Tab Switching
     ---------------------------------------------------------- */
  function initTabs() {
    document.querySelectorAll('.cpt-section').forEach(function (section) {
      if (section.dataset.cptTabsBound === 'true') return;
      section.dataset.cptTabsBound = 'true';

      var tabBtns = Array.prototype.slice.call(section.querySelectorAll('.cpt-tab-btn'));
      var panels = Array.prototype.slice.call(section.querySelectorAll('.cpt-panel'));
      if (!tabBtns.length || !panels.length) return;

      var sectionKey = section.id || section.dataset.sectionId || 'default';
      var storageKey = 'cpt-active-tab:' + sectionKey;

      function activateTab(tabId, options) {
        options = options || {};
        if (!tabId) return false;

        var selectedBtn = null;
        var targetPanel = null;

        tabBtns.forEach(function (b) {
          var isTarget = b.dataset.tabId === String(tabId);
          b.classList.toggle('is-active', isTarget);
          b.setAttribute('aria-selected', isTarget ? 'true' : 'false');
          if (isTarget) selectedBtn = b;
        });
        if (!selectedBtn) return false;

        panels.forEach(function (p) {
          var isTarget = p.dataset.tabId === String(tabId);
          p.classList.toggle('is-active', isTarget);
          if (isTarget) targetPanel = p;
        });

        if (targetPanel) {
          targetPanel.scrollTop = 0;
          if (options.scroll !== false) {
            var sectionTop = section.getBoundingClientRect().top + window.scrollY;
            if (window.scrollY > sectionTop) {
              window.scrollTo({ top: sectionTop, behavior: 'smooth' });
            }
          }
        }

        if (options.persist !== false) {
          try { sessionStorage.setItem(storageKey, String(tabId)); } catch (e) { }
        }
        return true;
      }

      section._cptActivateTab = activateTab;

      tabBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          activateTab(btn.dataset.tabId, { scroll: true, persist: true });
        });
      });

      var restoredTabId = null;
      try { restoredTabId = sessionStorage.getItem(storageKey); } catch (e) { }
      if (restoredTabId && activateTab(restoredTabId, { scroll: false, persist: false })) return;

      var defaultBtn = section.querySelector('.cpt-tab-btn.is-active') || tabBtns[0];
      if (defaultBtn) activateTab(defaultBtn.dataset.tabId, { scroll: false, persist: false });
    });

    if (window.Shopify && window.Shopify.designMode && !window.__cptEditorEventsBound) {
      window.__cptEditorEventsBound = true;

      document.addEventListener('shopify:block:select', function (event) {
        var selectedBlock = event.target;
        if (!selectedBlock || !selectedBlock.closest) return;
        var panel = selectedBlock.closest('.cpt-panel');
        if (!panel) return;
        var section = panel.closest('.cpt-section');
        if (!section || typeof section._cptActivateTab !== 'function') return;
        section._cptActivateTab(panel.dataset.tabId, { scroll: false, persist: true });
      });

      document.addEventListener('shopify:section:load', function () {
        initTabs();
        initChips();
        initBundleSelectors();
        initATC();
      });
    }

  }
  /* ----------------------------------------------------------
     1-Unit: Variant Chip Selection
     ---------------------------------------------------------- */
  function initChips() {
    document.querySelectorAll('.cpt-chips').forEach(function (chipGroup) {
      var chips = chipGroup.querySelectorAll('.cpt-chip');
      var card = chipGroup.closest('.cpt-promo-card');
      chips.forEach(function (chip) {
        if (chip.dataset.cptChipBound === 'true') return;
        chip.dataset.cptChipBound = 'true';
        chip.addEventListener('click', function () {
          if (isUnavailableChoice(chip)) return;

          markSelectedChip(chips, chip);

          if (card) updateCardVariant(card, chip);
        });
      });

      if (card) {
        var selectedChip = chipGroup.querySelector('.cpt-chip.is-selected:not(.is-unavailable)');
        if (!selectedChip) selectedChip = chipGroup.querySelector('.cpt-chip:not(.is-unavailable)');
        if (selectedChip) {
          markSelectedChip(chips, selectedChip);
          updateCardVariant(card, selectedChip);
        }
      }
    });
  }

  function updateCardVariant(card, chip) {
    var variantId      = chip.dataset.variantId;
    var priceRaw       = parseInt(chip.dataset.price || 0, 10);        /* in cents */
    var comparePriceRaw = parseInt(chip.dataset.comparePrice || 0, 10);
    var imageSrc       = chip.dataset.image;
    var productTitle   = chip.dataset.productTitle;
    var productUrl     = chip.dataset.productUrl;
    var variantTitle   = chip.dataset.variantTitle;
    var isAvailable    = !isUnavailableChoice(chip);
    var discountType   = normalizeDiscountType(card.dataset.discountType);
    var discountValue  = parseFloat(card.dataset.discountValue || 0);
    var promoMode      = card.dataset.promoMode;

    /* Update image */
    if (imageSrc) {
      var img = card.querySelector('.cpt-product-image img');
      if (img) img.src = imageSrc;
    }
    var productLink = card.querySelector('.cpt-product-image');
    if (productLink && productUrl) productLink.href = productUrl;

    var pTitle = card.querySelector('.cpt-product-name');
    if (pTitle && productTitle) pTitle.textContent = productTitle;

    /* Update variant label */
    var vLabel = card.querySelector('.cpt-variant-label');
    if (vLabel) {
      var cleanVariantTitle = (variantTitle || '').trim();
      if (cleanVariantTitle.toLowerCase() === 'default title') cleanVariantTitle = '';
      vLabel.textContent = cleanVariantTitle ? (' - ' + cleanVariantTitle) : '';
    }

    /* Calculate promo price */
    var originalCents = priceRaw;
    var promoCents = originalCents;

    if (promoMode === 'bundle_2') {
      if (discountType === 'percent') {
        promoCents = Math.ceil((originalCents * 2) * (1 - discountValue / 100));
      } else if (discountType === 'fixed_price') {
        promoCents = Math.round(discountValue * 100);
      } else if (discountType === 'fixed_amount') {
        promoCents = (originalCents * 2) - Math.round(discountValue * 100);
      }
    } else {
      if (discountType === 'percent') {
        promoCents = Math.ceil(originalCents * (1 - discountValue / 100));
      } else if (discountType === 'fixed_amount') {
        promoCents = originalCents - Math.round(discountValue * 100);
      }
    }

    promoCents = Math.max(0, promoCents);
    var savedCents = promoMode === 'bundle_2' ? ((originalCents * 2) - promoCents) : (originalCents - promoCents);

    /* Update DOM */
    var priceNew = card.querySelector('.cpt-price-new');
    var priceOld = card.querySelector('.cpt-price-old');
    var savings  = card.querySelector('.cpt-savings');

    if (priceNew) priceNew.textContent = formatMoney(promoCents);
    if (priceOld) {
      if (originalCents !== promoCents) {
        priceOld.textContent = formatMoney(comparePriceRaw || originalCents);
        priceOld.style.display = '';
      } else {
        priceOld.style.display = 'none';
      }
    }
    if (savings) {
      if (savedCents > 0) {
        savings.textContent = t('save_amount', { amount: formatMoney(savedCents) });
        savings.style.display = '';
      } else {
        savings.style.display = 'none';
      }
    }

    /* Update ATC button */
    var atcBtn = card.querySelector('.cpt-btn-atc');
    if (atcBtn) {
      atcBtn.dataset.variantId = variantId;
      atcBtn.disabled = !isAvailable;
      if (isAvailable) {
        atcBtn.textContent = promoMode === 'bundle_2' ? t('add_bundle') : t('add_to_cart');
      } else {
        atcBtn.textContent = t('sold_out');
      }
      atcBtn.dataset.originalText = atcBtn.textContent;
    }
  }

  /* ----------------------------------------------------------
     2-unit & 4-unit: Bundle / Mix-Match / BXGY Selection
     ---------------------------------------------------------- */
  function initBundleSelectors() {
    document.querySelectorAll('.cpt-bundle-block').forEach(function (block) {
      var promoMode   = block.dataset.promoMode;   /* mix_match | bxgy */
      var discountType = normalizeDiscountType(block.dataset.discountType);
      var discountValue = parseFloat(block.dataset.discountValue || 0);
      var buyQty      = parseInt(block.dataset.buyQty || 1, 10);
      var getQty      = parseInt(block.dataset.getQty || 0, 10);
      var totalSelect = buyQty + getQty;
      var atcBtn      = block.querySelector('.cpt-btn-atc');
      var clearBtn    = block.querySelector('.cpt-clear-btn');
      var items       = block.querySelectorAll('.cpt-product-grid-item');
      block._cptSelections = [];

      items.forEach(function (item) {
        if (item.dataset.cptBundleBound === 'true') {
          var existingUnavailable = item.classList.contains('is-unavailable') || item.dataset.available === 'false';
          item.setAttribute('tabindex', existingUnavailable ? '-1' : '0');
          return;
        }
        item.dataset.cptBundleBound = 'true';
        var isUnavailable = item.classList.contains('is-unavailable') || item.dataset.available === 'false';
        item.setAttribute('tabindex', isUnavailable ? '-1' : '0');

        function activateItem() {
          if (item.classList.contains('is-unavailable') || item.dataset.available === 'false') return;
          var selections = block._cptSelections || [];
          if (selections.length >= totalSelect) selections.shift();
          selections.push({
            variantId: item.dataset.variantId,
            price: parseInt(item.dataset.price || 0, 10),
            image: (item.querySelector('img') && item.querySelector('img').getAttribute('src')) || ''
          });
          block._cptSelections = selections;

          renderBundleSelections(block, selections, buyQty, promoMode);
          updateBundleSummary(block, selections, buyQty, totalSelect, promoMode, discountType, discountValue);
          setBundleATCState(block, atcBtn, buyQty);
        }

        item.addEventListener('click', activateItem);
        item.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          activateItem();
        });
      });

      if (clearBtn) {
        if (clearBtn.dataset.cptClearBound === 'true') {
          setBundleATCState(block, atcBtn, buyQty);
          return;
        }
        clearBtn.dataset.cptClearBound = 'true';
        clearBtn.addEventListener('click', function () {
          block._cptSelections = [];
          renderBundleSelections(block, [], buyQty, promoMode);
          updateBundleSummary(block, [], buyQty, totalSelect, promoMode, discountType, discountValue);
          setBundleATCState(block, atcBtn, buyQty);
        });
      }

      setBundleATCState(block, atcBtn, buyQty);
    });
  }


  function setBundleATCState(block, atcBtn, buyQty) {
    if (!atcBtn) return;
    var availableCount = block.querySelectorAll('.cpt-product-grid-item:not(.is-unavailable)').length;
    var allowDuplicates = block.dataset.allowDuplicates !== 'false';
    var addLabel = block.dataset.addLabel || t('add_bundle');
    var soldOutLabel = block.dataset.soldOutLabel || t('sold_out');
    var selections = block._cptSelections || [];

    if (availableCount === 0 || (!allowDuplicates && availableCount < buyQty)) {
      atcBtn.disabled = true;
      atcBtn.textContent = soldOutLabel;
      return;
    }

    atcBtn.textContent = addLabel;
    atcBtn.disabled = selections.length < buyQty;
  }

  function renderBundleSelections(block, selections, buyQty, promoMode) {
    var qtyById = {};
    var freeById = {};
    selections.forEach(function (sel, idx) {
      qtyById[sel.variantId] = (qtyById[sel.variantId] || 0) + 1;
      if (promoMode === 'bxgy' && idx >= buyQty) {
        freeById[sel.variantId] = (freeById[sel.variantId] || 0) + 1;
      }
    });

    block.querySelectorAll('.cpt-product-grid-item').forEach(function (el) {
      var vid = el.dataset.variantId;
      var qty = qtyById[vid] || 0;
      var freeQty = freeById[vid] || 0;
      var freeBadge = el.querySelector('.cpt-item-free-badge');
      el.classList.toggle('is-selected', qty > 0);
      el.classList.toggle('is-free', freeQty > 0);
      el.setAttribute('aria-checked', qty > 0 ? 'true' : 'false');
      el.setAttribute('data-selected-qty', qty);
      if (freeBadge) freeBadge.style.display = freeQty > 0 ? '' : 'none';
    });

    var selectedWrap = block.querySelector('.cpt-selected-items');
    if (selectedWrap) {
      selectedWrap.innerHTML = selections.map(function (sel) {
        var src = sel.image || '';
        return '<span class="cpt-selected-item">' + (src ? ('<img src="' + src + '" alt="">') : '') + '</span>';
      }).join('');
    }
  }

  function updateBundleSummary(block, selections, buyQty, totalSelect, promoMode, discountType, discountValue) {
    var summaryText = block.querySelector('.cpt-bundle-summary-text');
    var pricingWrap = block.querySelector('.cpt-bundle-pricing');
    var oldEl = block.querySelector('.cpt-bundle-price-old');
    var newEl = block.querySelector('.cpt-bundle-price-new');
    var saveEl = block.querySelector('.cpt-bundle-save-chip');
    if (!summaryText) return;

    var selCount   = selections.length;
    var remaining  = buyQty - selCount;

    if (remaining > 0) {
      summaryText.textContent = t('select_more', { n: remaining });
    } else if (promoMode === 'bxgy' && selCount < totalSelect) {
      summaryText.textContent = t('select_free', { n: totalSelect - selCount });
    } else {
      summaryText.textContent = t('ready_to_add');
    }

    if (!pricingWrap) return;
    if (selCount < buyQty) {
      pricingWrap.style.display = 'none';
      return;
    }

    var basePaid = 0;
    for (var i = 0; i < buyQty; i++) basePaid += (selections[i] ? selections[i].price : 0);
    var charge = basePaid;
    if (promoMode === 'mix_match') {
      if (discountType === 'fixed_price') {
        charge = Math.round(discountValue * 100);
      } else if (discountType === 'fixed_amount') {
        charge = Math.round(basePaid - (discountValue * 100));
      } else if (discountType === 'percent') {
        charge = Math.round(basePaid * (1 - (discountValue / 100)));
      }
    }
    if (promoMode === 'bxgy') {
      charge = basePaid;
    }
    charge = Math.max(0, charge);
    var saved = Math.max(0, basePaid - charge);

    if (oldEl) {
      oldEl.textContent = formatMoney(basePaid);
      oldEl.style.display = saved > 0 ? '' : 'none';
    }
    if (newEl) newEl.textContent = formatMoney(charge);
    if (saveEl) {
      saveEl.textContent = t('save_amount', { amount: formatMoney(saved) });
      saveEl.style.display = saved > 0 ? '' : 'none';
    }
    pricingWrap.style.display = '';
  }

  /* ----------------------------------------------------------
     AJAX Add to Cart
     ---------------------------------------------------------- */
  function initATC() {
    if (window.__cptATCBound) return;
    window.__cptATCBound = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.cpt-btn-atc');
      if (!btn) return;
      if (btn.disabled || btn.classList.contains('is-loading')) return;

      e.preventDefault();

      var bundleBlock = btn.closest('.cpt-bundle-block');
      if (bundleBlock) {
        addBundleToCart(btn, bundleBlock);
      } else {
        addSingleToCart(btn);
      }
    });
  }

  function addSingleToCart(btn) {
    var card       = btn.closest('.cpt-promo-card');
    var variantId  = btn.dataset.variantId;
    var quantity   = parseInt(btn.dataset.quantity || 1, 10);

    if (!variantId) { showError(btn, t('no_variant_selected')); return; }

    setLoading(btn, true);

    cartAdd({ id: variantId, quantity: quantity }, function (err) {
      setLoading(btn, false);
      if (err) { showError(btn, err); }
      else {
        flashAdded(btn);
        refreshCartCount();
      }
    });
  }

  function addBundleToCart(btn, block) {
    var selected = block._cptSelections || [];
    var qtyById = {};
    selected.forEach(function (sel) {
      qtyById[sel.variantId] = (qtyById[sel.variantId] || 0) + 1;
    });
    var items = Object.keys(qtyById).map(function (id) {
      return { id: id, quantity: qtyById[id] };
    });

    if (items.length === 0) return;

    setLoading(btn, true);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      setLoading(btn, false);
      if (data.status) {
        showError(btn, data.description || t('cart_error'));
      } else {
        flashAdded(btn);
        refreshCartCount();
        /* Reset selections */
        block.querySelectorAll('.cpt-product-grid-item').forEach(function (el) {
          el.classList.remove('is-selected', 'is-free');
          el.setAttribute('data-selected-qty', '0');
          var freeBadge = el.querySelector('.cpt-item-free-badge');
          if (freeBadge) freeBadge.style.display = 'none';
        });
        var selectedWrap = block.querySelector('.cpt-selected-items');
        if (selectedWrap) selectedWrap.innerHTML = '';
        var pricingWrap = block.querySelector('.cpt-bundle-pricing');
        if (pricingWrap) pricingWrap.style.display = 'none';
        var summaryText = block.querySelector('.cpt-bundle-summary-text');
        var buyQty = parseInt(block.dataset.buyQty || 1, 10);
        if (summaryText) summaryText.textContent = t('select_more', { n: buyQty });
        block._cptSelections = [];
        setBundleATCState(block, btn, buyQty);
      }
    })
    .catch(function () {
      setLoading(btn, false);
      showError(btn, t('network_error'));
    });
  }

  function cartAdd(payload, cb) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      if (data.status) cb(data.description || t('cart_error'));
      else cb(null, data);
    })
    .catch(function () { cb(t('network_error')); });
  }

  function refreshCartCount() {
    fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (cart) {
      if (!cart) return;

      var count = cart.item_count || 0;

      var selectors = [
        '.cart-count-bubble span[aria-hidden]',
        '.cart-count-bubble span:not(.visually-hidden)',
        '[data-cart-count]',
        '.header__cart-count',
        'cart-notification-button .cart-count-bubble span',
        '#cart-icon-bubble .cart-count-bubble',
        '#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]',
        '.cart-item-count'
      ];
      selectors.forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (el) {
          el.textContent = count;
        });
      });

      var sectionUrl = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/') + '?sections=cart-icon-bubble';
      fetch(sectionUrl, {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (payload) {
        if (!payload || !payload['cart-icon-bubble']) return;
        var host = document.getElementById('cart-icon-bubble');
        if (!host) return;
        var tpl = document.createElement('template');
        tpl.innerHTML = payload['cart-icon-bubble'];
        var fresh = tpl.content.querySelector('#cart-icon-bubble') || tpl.content.firstElementChild;
        if (fresh) host.innerHTML = fresh.innerHTML;
      })
      .catch(function () {});

      document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true, detail: { cart: cart } }));
    })
    .catch(function () {});
  }

  /* ----------------------------------------------------------
     UI Helpers
     ---------------------------------------------------------- */
  function setLoading(btn, state) {
    btn.classList.toggle('is-loading', state);
  }

  function flashAdded(btn) {
    var orig = btn.dataset.originalText || btn.textContent;
    btn.dataset.originalText = orig;
    btn.classList.add('is-added');
    btn.textContent = t('added_to_cart');
    setTimeout(function () {
      btn.classList.remove('is-added');
      btn.textContent = orig;
    }, 2200);
  }

  function showError(btn, msg) {
    var existing = btn.parentNode.querySelector('.cpt-error-msg');
    if (existing) existing.remove();
    var el = document.createElement('div');
    el.className = 'cpt-error-msg';
    el.textContent = msg;
    btn.insertAdjacentElement('afterend', el);
    setTimeout(function () { el.remove(); }, 4000);
  }

  /* ----------------------------------------------------------
     Boot
     ---------------------------------------------------------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  function boot() {
    stabilizeInitialScroll();
    initTabs();
    initChips();
    initBundleSelectors();
    initATC();
  }

})();

